// "All departments" view. The API answers for one department at a time, so the UI asks for each
// department and combines the answers here. Nothing is stored; it is read-only.

function tag(rows, dept) {
  return rows.map((r) => ({ ...r, departmentId: dept.id, department: dept.name }));
}

/** Lists (applications, risks, vendors…): every department's rows, each marked with its department. */
export function mergeLists(parts) {
  return parts.flatMap(({ dept, data }) => tag(data || [], dept));
}

/** Frameworks: one card per framework code, with the counts added up across departments. */
export function mergeFrameworks(parts) {
  const byCode = new Map();
  parts.forEach(({ data }) =>
    (data || []).forEach((f) => {
      const cur = byCode.get(f.code) || {
        ...f, id: f.code, totalControls: 0, implemented: 0, partiallyImplemented: 0, notImplemented: 0, notApplicable: 0,
      };
      cur.totalControls += f.totalControls;
      cur.implemented += f.implemented;
      cur.partiallyImplemented += f.partiallyImplemented;
      cur.notImplemented += f.notImplemented;
      cur.notApplicable += f.notApplicable;
      byCode.set(f.code, cur);
    })
  );
  return [...byCode.values()].map((f) => {
    const applicable = f.totalControls - f.notApplicable;
    return { ...f, compliancePct: applicable ? Math.round(((f.implemented + 0.5 * f.partiallyImplemented) * 100) / applicable) : 0 };
  });
}

function sumBreakdowns(lists) {
  const map = new Map();
  lists.forEach((list) =>
    (list || []).forEach((s) => {
      const cur = map.get(s.key) || { ...s, count: 0 };
      cur.count += s.count;
      map.set(s.key, cur);
    })
  );
  return [...map.values()];
}

const BREAKDOWNS = [
  "riskTierBreakdown", "workflowBreakdown", "controlBreakdown", "evidenceBreakdown",
  "riskLevelBreakdown", "findingSeverityBreakdown", "vendorTierBreakdown",
];

/** Dashboard: counts are added up, percentages are weighted by how many controls each department has. */
export function mergeDashboards(parts) {
  const ok = parts.filter((p) => p.data);
  const kpis = {};
  ok.forEach(({ data }) =>
    Object.entries(data.kpis).forEach(([k, v]) => {
      if (k !== "controlCompliancePct") kpis[k] = (kpis[k] || 0) + v;
    })
  );

  const weight = (d) => d.frameworkCompliance.reduce((n, f) => n + f.totalControls, 0) || 1;
  const totalWeight = ok.reduce((n, { data }) => n + weight(data), 0) || 1;
  kpis.controlCompliancePct = Math.round(ok.reduce((n, { data }) => n + data.kpis.controlCompliancePct * weight(data), 0) / totalWeight);

  const fw = new Map();
  ok.forEach(({ data }) =>
    data.frameworkCompliance.forEach((f) => {
      const cur = fw.get(f.code) || { code: f.code, name: f.name, totalControls: 0, weighted: 0 };
      cur.totalControls += f.totalControls;
      cur.weighted += f.compliancePct * f.totalControls;
      fw.set(f.code, cur);
    })
  );

  // Take one entry from each department in turn so no single department fills the list.
  const roundRobin = (lists, limit) => {
    const out = [];
    for (let i = 0; out.length < limit && lists.some((l) => i < l.length); i++) {
      lists.forEach((l) => { if (i < l.length && out.length < limit) out.push(l[i]); });
    }
    return out;
  };

  const merged = {
    clientName: "All departments",
    kpis,
    frameworkCompliance: [...fw.values()].map((f) => ({
      code: f.code, name: f.name, totalControls: f.totalControls,
      compliancePct: f.totalControls ? Math.round(f.weighted / f.totalControls) : 0,
    })),
    topRisks: ok
      .flatMap(({ dept, data }) => tag(data.topRisks, dept))
      .sort((a, b) => b.score - a.score)
      .slice(0, 5),
    applicationsNeedingAttention: roundRobin(ok.map(({ dept, data }) => tag(data.applicationsNeedingAttention, dept)), 6),
  };
  BREAKDOWNS.forEach((b) => { merged[b] = sumBreakdowns(ok.map(({ data }) => data[b])); });
  return merged;
}
